import React, { useState } from 'react';
import { Box, Typography, Radio, RadioGroup, FormControlLabel, FormLabel, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import ProceedButton from '../../Components/Buttons/ProceedButton';
import { useAuth } from '../../Context/AuthContext';
import { FOODIMETRIC_HOST_URL } from '../../Utils/host';
import { Helmet } from 'react-helmet-async';

// Custom styled Radio
const CustomRadio = styled(Radio)({
    color: '#3a8589',
    '&.Mui-checked': {
        color: '#3a8589',
    },
});

const EE = () => {
    const { user, logout } = useAuth();
    const [weight, setWeight] = useState(70);
    const [height, setHeight] = useState(170);
    const [age, setAge] = useState(25);
    const [gender, setGender] = useState('male');
    const [activityLevel, setActivityLevel] = useState(1.2); // Sedentary
    const [tee, setTee] = useState(null);

    const handleWeightChange = (event) => {
        setWeight(event.target.value);
    };

    const handleHeightChange = (event) => {
        setHeight(event.target.value);
    };

    const handleAgeChange = (event) => {
        setAge(event.target.value);
    };

    const handleGenderChange = (e) => setGender(e.target.value);

    const handleActivityLevelChange = (e) => setActivityLevel(parseFloat(e.target.value));

    const calculateTEE = () => {
        let bmr;
        // const heightInMeters = height / 100;

        if (gender === 'male') {
            bmr = 88.362 + (13.397 * weight) + (4.799 * height) - (5.677 * age);
        } else {
            bmr = 447.593 + (9.247 * weight) + (3.098 * height) - (4.330 * age);
        }

        // Calculate TEE
        const teeValue = bmr * activityLevel;
        setTee(teeValue);
    };

    const handleProceed = async () => {
        calculateTEE(); // Calculate TEE

        if (tee === null) return; // Ensure TEE is calculated before sending data

        const calculationPayload = {
            user_id: user._id, // Replace `user._id` with the actual user ID
            calculator_name: "EE",
            parameters: {
                weight: `${weight} kg`,
                height: `${height} cm`,
                age: `${age} years`,
                gender: gender,
                activity_level: activityLevel,
            },
            result: `${tee.toFixed(2)} kcal/day`,
            calculation_details: "TEE calculated using the Harris-Benedict formula adjusted for activity level",
        };

        try {
            const response = await fetch(`${FOODIMETRIC_HOST_URL}/calculations`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user.token}`, // Replace with the actual token
                },
                body: JSON.stringify(calculationPayload),
            });

            if (response.status === 401) {
                logout();
                window.location.href = "/login";
                return; // Prevent further code execution
            }
            if (response.ok) {
                await response.json();
                // console.log("Calculation saved:", data);
            } else {
                const error = await response.json();
                console.error("Failed to save calculation:", error);
            }
        } catch (err) {
            console.error("Error saving calculation:", err);
        }
    };

    return (
        <main className="py-8">
            <Helmet>
                <title>EE Calculator - Daily Energy Expenditure by Weight, Height & Activity</title>
                <meta name="description" content="Accurately estimate your daily caloric needs using our Total Energy Expenditure (TEE) Calculator. Input your weight, height, age, gender, and activity level to plan your nutrition effectively." />
                <meta name="keywords" content="TEE calculator, total energy expenditure, calculate daily calories, Harris-Benedict calculator, weight management, caloric needs, health and fitness tool, Foodimetric" />
            </Helmet>
            <div className="bg-white p-8 min-h-screen">
                <Box sx={{ display: 'none' }}>
                    <Typography>
                        Energy expenditure calculator for men and women | TEE calculator | How to calculate calories burned per day | Harris-Benedict equation | Weight loss calculator | Nutrition and fitness tools | BMR and activity level calculator | Daily caloric needs | Foodimetric energy calculator
                    </Typography>
                </Box>
                <Box sx={{
                    maxWidth: { xs: '100%' },
                    mx: 'auto',
                    px: { xs: 2, sm: 4 },
                    py: 4,
                    // textAlign: 'center',
                    fontFamily: 'Outfit, serif',
                }}>
                    <Typography variant="h4" sx={{ fontFamily: 'Itim, cursive', mb: 2 }}>
                        Energy Expenditure (EE) Calculator
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'Outfit, serif', mb: 4 }}>
                        Want to know how many calories your body burns in a day? Our Energy Expenditure (EE) calculator uses the Harris-Benedict equation to estimate how many calories you need based on your weight, height, age, gender, and activity level. Whether you're trying to lose weight, gain muscle, or maintain your current physique, understanding your energy needs is key to achieving your health goals.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Itim, cursive' }}>
                        Weight (kg)
                    </Typography>
                    <TextField
                        value={weight}
                        onChange={handleWeightChange}
                        label="Weight"
                        type="number"
                        fullWidth
                        variant="outlined"
                        margin="normal"
                    />
                    <Typography variant="body1" sx={{ fontFamily: 'Outfit, serif' }}
                    >Current Weight: {weight} kg</Typography>

                    <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Itim, cursive' }}>
                        Height (cm)
                    </Typography>
                    <TextField
                        value={height}
                        onChange={handleHeightChange}
                        label="Height"
                        type="number"
                        fullWidth
                        variant="outlined"
                        margin="normal"
                    />
                    <Typography variant="body1" sx={{ fontFamily: 'Outfit, serif' }}
                    >Current Height: {height} cm</Typography>

                    <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Itim, cursive' }}>
                        Age (years)
                    </Typography>
                    <TextField
                        value={age}
                        onChange={handleAgeChange}
                        label="Age"
                        type="number"
                        fullWidth
                        variant="outlined"
                        margin="normal"
                    />
                    <Typography variant="body1" sx={{ fontFamily: 'Outfit, serif' }}
                    >Current Age: {age} years</Typography>

                    <Box sx={{ textAlign: 'left', mt: 2 }}>
                        <FormLabel component="legend">Gender</FormLabel>
                        <RadioGroup
                            row
                            value={gender}
                            onChange={handleGenderChange}
                            aria-label="gender"
                            name="gender"
                        >
                            <FormControlLabel value="male" control={<CustomRadio />} label="Male" />
                            <FormControlLabel value="female" control={<CustomRadio />} label="Female" />
                        </RadioGroup>
                    </Box>

                    <Box sx={{ textAlign: 'left', mt: 2 }}>
                        <FormLabel component="legend" sx={{ fontFamily: 'Itim, cursive' }}>Activity Level</FormLabel>
                        <RadioGroup
                            row
                            value={activityLevel}
                            onChange={handleActivityLevelChange}
                            aria-label="activity level"
                            name="activity level"
                        >
                            <FormControlLabel value={1.2} control={<CustomRadio />} label="Sedentary" sx={{ fontFamily: 'Outfit, serif' }} />
                            <FormControlLabel value={1.375} control={<CustomRadio />} label="Lightly Active" sx={{ fontFamily: 'Outfit, serif' }} />
                            <FormControlLabel value={1.55} control={<CustomRadio />} label="Moderately Active" sx={{ fontFamily: 'Outfit, serif' }} />
                            <FormControlLabel value={1.725} control={<CustomRadio />} label="Very Active" sx={{ fontFamily: 'Outfit, serif' }} />
                            <FormControlLabel value={1.9} control={<CustomRadio />} label="Extra Active" sx={{ fontFamily: 'Outfit, serif' }} />
                        </RadioGroup>
                    </Box>

                    <ProceedButton color="#ffba08" type="button" auth="authorized" onClick={handleProceed} />

                    {tee !== null && (
                        <div>
                            <Typography variant="h6" sx={{ mt: 4, fontFamily: 'Itim, cursive' }}>
                                Your Total Energy Expenditure is: {tee.toFixed(2)} kcal/day
                            </Typography>
                            <Typography sx={{ mt: 2, fontSize: '1rem', color: 'gray', fontFamily: 'Outfit, serif' }}>
                                BMR Formula Used:
                            </Typography>
                            <Typography sx={{ mt: 1, fontStyle: 'italic', color: '#555', fontFamily: 'Outfit, serif' }}>
                                Mifflin-St Jeor Equation
                            </Typography>
                        </div>
                    )}
                </Box>
                <Box sx={{ mt: 6 }}>
                    <Typography variant="h5" sx={{ fontFamily: 'Itim, cursive', mb: 2 }}>
                        Frequently Asked Questions
                    </Typography>

                    <Typography variant="h6" sx={{ fontFamily: 'Outfit, serif', mt: 2 }}>
                        What is Total Energy Expenditure (TEE)?
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, serif' }}>
                        Total Energy Expenditure is the number of calories your body uses in a day. It includes your Basal Metabolic Rate (BMR), physical activity, and digestion (also called the thermic effect of food).
                    </Typography>

                    <Typography variant="h6" sx={{ fontFamily: 'Outfit, serif', mt: 2 }}>
                        How is TEE calculated?
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, serif' }}>
                        This calculator uses the Harris-Benedict equation to estimate your BMR and then multiplies it by your activity factor to get your TEE.
                    </Typography>

                    <Typography variant="h6" sx={{ fontFamily: 'Outfit, serif', mt: 2 }}>
                        Why should I know my TEE?
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, serif' }}>
                        Knowing your TEE helps you create a personalized meal plan. If you eat fewer calories than your TEE, you'll lose weight. If you eat more, you'll gain weight.
                    </Typography>

                    <Typography variant="h6" sx={{ fontFamily: 'Outfit, serif', mt: 2 }}>
                        How accurate is this calculator?
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, serif' }}>
                        While it's a helpful estimate, actual calorie needs can vary due to genetics, muscle mass, and other factors. Use this as a starting point and adjust as needed.
                    </Typography>
                </Box>
            </div>
        </main>
    );
};

export default EE;
